// src/components/store/sections/product-cards/Variant41DiagonalCutOrbSunsetAmberCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingCart, Zap, Heart, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

export default function Variant41DiagonalCutOrbSunsetAmberCard({
  product,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    comparePrice,
    discountPercent,
    added,
    wishlisted,
    setWishlisted,
    handleAddToCart,
    handleBuyNow,
  } = useCardActions(product, subdomain);

  return (
    <div className="relative w-full h-full bg-white rounded-[30px] border-2 border-amber-200 p-4 shadow-md hover:shadow-2xl transition-all duration-300 flex flex-col justify-between overflow-hidden group select-none">
      <div
        className="absolute top-0 right-0 w-36 h-36 bg-gradient-to-bl from-amber-500 to-orange-600 opacity-90 pointer-events-none transition-transform duration-500 group-hover:scale-110"
        style={{ clipPath: "polygon(18% 0, 100% 0, 100% 82%)" }}
      />
      <div className="relative z-10 flex items-center justify-between">
        <span className="px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-black uppercase tracking-wider border border-amber-200">
          -{discountPercent}% OFF
        </span>
        <button
          type="button"
          onClick={() => setWishlisted(!wishlisted)}
          className="w-7 h-7 rounded-full bg-white/90 shadow-xs flex items-center justify-center text-slate-400 hover:text-rose-500 cursor-pointer"
        >
          <Heart className={`w-3.5 h-3.5 ${wishlisted ? "fill-rose-500 text-rose-500" : ""}`} />
        </button>
      </div>

      <Link href={productUrl} className="relative z-10 w-full h-44 my-2 flex items-center justify-center block">
        <div className="w-36 h-36 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center relative shadow-inner group-hover:scale-105 transition-transform">
          <div className="relative w-32 h-32">
            <Image src={productImg} alt={productName} fill unoptimized className="object-contain drop-shadow-lg" />
          </div>
        </div>
      </Link>

      <div className="relative z-10 space-y-1 text-left">
        <div className="flex items-center justify-between text-[10px] font-bold text-slate-400">
          <span className="uppercase truncate max-w-[120px]">{categoryName}</span>
          <span className="flex items-center gap-0.5 text-amber-500">
            <Star className="w-3 h-3 fill-amber-400" /> {rating.toFixed(1)}
          </span>
        </div>
        <Link href={productUrl} className="block">
          <h3 className="text-sm font-black text-slate-900 truncate hover:underline">{productName}</h3>
        </Link>
        <div className="flex items-baseline gap-2 pt-1 font-mono">
          <span className="text-lg font-black text-amber-800">৳{currentPrice.toLocaleString()}</span>
          {comparePrice > currentPrice && (
            <span className="text-xs text-slate-400 line-through">৳{comparePrice.toLocaleString()}</span>
          )}
        </div>
      </div>

      <div className="relative z-10 flex items-center gap-2 pt-3 mt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={(e) => handleAddToCart(e)}
          className="flex-1 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-black text-[10px] uppercase tracking-wider shadow-sm active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1"
        >
          {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />}
          <span>{added ? "Added" : "Add Cart"}</span>
        </button>
        <button
          type="button"
          onClick={(e) => handleBuyNow(e)}
          className="p-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white active:scale-95 transition-all cursor-pointer"
          title="Buy Now"
        >
          <Zap className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
        </button>
      </div>
    </div>
  );
}
